import { useEffect, useMemo, useState } from 'react';
import Breadcrumb from '@components/Shared/Breadcrumb';
import { Card, CardBody, CardFooter, CardHeader } from '@components/card/card';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Loading from '@components/Shared/Loader/Loading';

import { useAppDispatch, useAppSelector } from '@store/hooks';
import { getPagedData, setEndpoint, setFilters } from '@store/DynamicSlice/dynamicTableSlice';

import { DynamicFilter } from '@Helper/DynamicFilter/DynamicFilter';
import { DynamicPagination } from '@Helper/DynamicFilter/DynamicPagination';

import Select from 'react-select';
import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';
import useGetSuppliersList from '@hooks/NTX/Peoples/useGetSuppliersList';

import { Can } from '@Permisstions/Can';
import NeatixModal from '@components/Modal/NeatixModal';
import PoList from './PoList';

const TABLE_KEY = 'PurchaseOrder_ReturnFromPO_Index';

const Index = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [showPoList, setShowPoList] = useState(false);

  const { OperatingUnitOptions } = useGetOperatingUnitWithPermission();
  const { OptionSuppliers } = useGetSuppliersList();

  // ✅ فلاتر أعلى الصفحة (UI)
  const [supplierFilter, setSupplierFilter] = useState<any>(null);
  const [organizationFilter, setOrganizationFilter] = useState<any>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const [showFilters, setShowFilters] = useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  const tableState = useAppSelector(state => state.dynamicTable[TABLE_KEY]);
  const data = tableState?.data ?? [];
  const loading = tableState?.loading ?? false;
  const error = tableState?.error ?? null;
  const filters = tableState?.filters ?? [];
  const endpoint = tableState?.endpoint;

  // (غالباً موجودة في slice)
  const pageNumber = tableState?.pageIndex;
  const pageSize = tableState?.pageSize;

  // ✅ signature للفلاتر (حتى ما تتأثر بتغيير id)
  const filtersSignature = useMemo(() => {
    const safe = (filters || []).map((f: any) => ({
      field: f?.field,
      operator: f?.operator,
      value: f?.value
    }));
    return JSON.stringify(safe);
  }, [filters]);

  // ✅ set endpoint مرة واحدة
  useEffect(() => {
    if (endpoint !== 'PurchaseOrder/GetList_paged') {
      dispatch(setEndpoint({ tableKey: TABLE_KEY, endpoint: 'ReturnsPurchaseOrder/GetList_paged' }));
      // dispatch(setEndpoint({ tableKey: TABLE_KEY, endpoint: 'PurchaseOrder/GetList_paged' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ أي تغير في filters/page => fetch
  useEffect(() => {
    if (!endpoint) return;
    dispatch(getPagedData({ tableKey: TABLE_KEY }));
  }, [dispatch, endpoint, pageNumber, pageSize, filtersSignature]);

  // ✅ عداد الفلاتر النشطة
  useEffect(() => {
    setActiveFiltersCount(filters.length);
  }, [filters]);

  // ✅ إعادة تعبئة فلاتر الـ UI من الـ store (عشان ما تعاني من reset)
  useEffect(() => {
    if (!filters?.length) return;

    const findOpt = (opts: any[], v: any) => opts?.find(o => o?.value === v) ?? null;

    const sup = filters.find((f: any) => f.field === 'peopleId' && f.operator === 0);
    const org = filters.find((f: any) => f.field === 'organizationId' && f.operator === 0);

    const from = filters.find((f: any) => f.field === 'transactionDate' && f.operator === 3);
    const to = filters.find((f: any) => f.field === 'transactionDate' && f.operator === 5);

    const code = filters.find((f: any) => f.field === 'code' && f.operator === 7);

    if (sup?.value && OptionSuppliers?.length) setSupplierFilter(findOpt(OptionSuppliers, sup.value));
    if (org?.value && OperatingUnitOptions?.length) setOrganizationFilter(findOpt(OperatingUnitOptions, org.value));

    if (from?.value) setDateFrom(String(from.value).slice(0, 10));
    if (to?.value) setDateTo(String(to.value).slice(0, 10));

    if (code?.value) setSearchFilter(String(code.value));
  }, [filtersSignature, OptionSuppliers, OperatingUnitOptions]);

  const generateFilterId = () => `filter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const buildFiltersFromUI = () => {
    const newFilters: any[] = [];

    if (supplierFilter) {
      newFilters.push({
        id: generateFilterId(),
        field: 'peopleId',
        operator: 0, // OpEquals
        value: supplierFilter.value
      });
    }

    if (organizationFilter) {
      newFilters.push({
        id: generateFilterId(),
        field: 'organizationId',
        operator: 0, // OpEquals
        value: organizationFilter.value
      });
    }

    if (dateFrom) {
      newFilters.push({
        id: generateFilterId(),
        field: 'transactionDate',
        operator: 3, // OpGreaterOrEqual
        value: dateFrom
      });
    }

    if (dateTo) {
      newFilters.push({
        id: generateFilterId(),
        field: 'transactionDate',
        operator: 5, // OpLessOrEqual
        value: dateTo
      });
    }

    if (searchFilter) {
      newFilters.push({
        id: generateFilterId(),
        field: 'code',
        operator: 7, // OpContains
        value: searchFilter
      });
    }

    return newFilters;
  };

  // ✅ لا تنادي getPagedData هنا (الجلب من useEffect)
  const applyFilters = () => {
    dispatch(setFilters({ tableKey: TABLE_KEY, filters: buildFiltersFromUI() }));
  };

  const clearFilters = () => {
    setSupplierFilter(null);
    setOrganizationFilter(null);
    setDateFrom('');
    setDateTo('');
    setSearchFilter('');

    dispatch(setFilters({ tableKey: TABLE_KEY, filters: [] }));
  };

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);

  const getReceiveStatusBadge = (item: any) => {
    const bothReceived = item.inventoryFlag && item.noneInventoryFlag;

    if (bothReceived) {
      return (
        <span className='badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center'>
          <i className='fa fa-check-circle fs-15px fa-fw me-5px'></i>
          {t('تم الاستلام')}
        </span>
      );
    }

    if (item.inventoryFlag === false) {
      return (
        <span className='badge border border-danger text-danger px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center'>
          <i className='fa fa-clock fs-15px fa-fw me-5px'></i>
          {t(' مخزني: لم يتم استلامه')}
        </span>
      );
    }

    if (item.noneInventoryFlag === false) {
      return (
        <span className='badge border border-danger text-danger px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center'>
          <i className='fa fa-clock fs-15px fa-fw me-5px'></i>
          {t(' غير مخزني : لم يتم استلامه')}
        </span>
      );
    }

    return <span className='text-muted'>-</span>;
  };

  // ✅ النوع: إذا متعدد -> "متعدد" ، غير ذلك -> اسم المخزن + "مخزن واحد"
  const getTypeOrInventoryDisplay = (item: any) => {
    if (item?.isMultiInventoryFlag) {
      return (
        <span className='badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center'>
          <i className='fa fa-layer-group fs-15px fa-fw me-5px'></i>
          {t('متعدد')}
        </span>
      );
    }

    const invName = i18n.language === 'en' ? item?.inventoryName : item?.inventoryName2;

    return (
      <div className='d-flex flex-column'>
        <span className='fw-bold1 text-theme1'>{invName || '-'}</span>
        {/* <small className='text-muted'>{t('مخزن واحد')}</small> */}
      </div>
    );
  };

  const fields: any = useMemo(
    () => [
      { name: 'code', label: t('Number'), type: 'string' },
      { name: 'transactionDate', label: t('Date'), type: 'date' },
      { name: 'peopleId', label: t('Supplier'), type: 'options', options: OptionSuppliers },
      { name: 'organizationId', label: t('Branch'), type: 'options', options: OperatingUnitOptions },
      {
        name: 'isMultiInventoryFlag',
        label: t('type'),
        type: 'options',
        options: [
          { value: true, label: t('متعدد') },
          { value: false, label: t('مخزن واحد') }
        ]
      }
    ],
    [t, OptionSuppliers, OperatingUnitOptions]
  );

  const breadcrumbList = [
    { name: 'Home', link: ':moduleHome' },
    { name: 'مردود مشتريات امر الشراء', link: null }
  ];

  const displayData = data;

  return (
    <>
      <div className='d-flex align-items-center'>
        <Breadcrumb BreadcrumbList={breadcrumbList} PageName='مردود مشتريات - من امر شراء' />

        <div className='ms-auto'>
          {/* <Can permission={'PURAddPurchaseRequests'}> */}
          <Link to='' className='btn btn-theme' onClick={() => setShowPoList(true)}>
            <i className='fa fa-plus-circle fa-fw me-1'></i>
            {t('مردود أمر شراء جديـد')}
          </Link>
          {/* </Can> */}
        </div>
      </div>

      {/* ✅ شريط الفلاتر (نفس أوامر الشراء) */}
      <Card className='mb-3 shadow bg-body1'>
        <CardHeader>
          <div className='row g-2'>
            {/* وحدة التشغيل */}
            <div className='col-lg-3'>
              <div className='input-group'>
                <span className='input-group-text fw-bold'>
                  <i className='fas fa-building'></i>
                </span>
                <Select
                  value={organizationFilter}
                  onChange={setOrganizationFilter}
                  options={OperatingUnitOptions}
                  placeholder={t('اختر وحدة التشغيـل')}
                  isClearable
                  isSearchable
                  className='flex-grow-1'
                  classNamePrefix='react-select'
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                />
              </div>
            </div>

            {/* المورد */}
            <div className='col-lg-3'>
              <div className='input-group'>
                <span className='input-group-text fw-bold'>
                  <i className='fas fa-truck'></i>
                </span>
                <Select
                  value={supplierFilter}
                  onChange={setSupplierFilter}
                  options={OptionSuppliers}
                  placeholder={t('اختر مورد')}
                  isClearable
                  isSearchable
                  className='flex-grow-1'
                  classNamePrefix='react-select'
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                />
              </div>
            </div>

            {/* التاريخ من وإلى */}
            <div className='col-lg-4'>
              <div className='input-group'>
                <span className='input-group-text'>
                  <i className='fas fa-calendar-alt text-muted'></i>
                </span>
                <input
                  type='date'
                  className='form-control border-start-0'
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  placeholder={t('From')}
                />
                <span className='input-group-text'>-</span>
                <input
                  type='date'
                  className='form-control'
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  placeholder={t('To')}
                />
              </div>
            </div>

            {/* أزرار */}
            <div className='col-lg-2 text-end'>
              <button
                className='btn btn-outline-secondary me-2'
                type='button'
                onClick={clearFilters}
                title={t('ClearFilters')}
                disabled={activeFiltersCount === 0}
              >
                <i className='fas fa-times fa-fw'></i>
              </button>

              <button
                className='btn btn-theme me-2'
                type='button'
                onClick={applyFilters}
                title={t('ApplyFilters')}
                disabled={!supplierFilter && !organizationFilter && !dateFrom && !dateTo && !searchFilter}
              >
                <i className='fas fa-filter fa-fw'></i>
              </button>

              <button
                className='btn btn-outline-theme w-md-auto'
                onClick={() => setShowFilters(true)}
                title={t('AdvancedFilters')}
              >
                <i className='fas fa-sliders-h fa-fw'></i>
              </button>
            </div>

            {/* بحث */}
            <div className='col-12'>
              <div className='input-group'>
                <span className='input-group-text fw-bold'>
                  <i className='fas fa-search'></i>
                </span>
                <input
                  className='form-control'
                  value={searchFilter}
                  onChange={e => setSearchFilter(e.target.value)}
                  placeholder={t('بحث برقم مردود أمر الشراء (RPO...)')}
                />
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* ✅ الجدول */}
      <Card className='mb-4'>
        <Loading loading={loading} error={error}>
          <CardBody className='p-0'>
            <div className='table-responsive'>
              <table className='table table-hover mb-0'>
                <thead className='bg-theme bg-opacity-25 fw-bold'>
                  <tr>
                    <th className='small'>{t('Number')}</th>
                    <th className='small'>{t('Date')}</th>
                    <th className='small'>{t('Supplier')}</th>
                    <th className='small'>{t('وحدة التشغيـل')}</th>
                    <th className='small'>{t('المخزن')}</th>
                    <th className='small'>{t('Amount')}</th>
                    <th className='small'>{t('Status')}</th>
                    <th className='small text-center'>{t('Actions')}</th>
                  </tr>
                </thead>

                <tbody>
                  {displayData.length > 0 ? (
                    displayData.map((item: any) => (
                      <tr key={item.id}>
                        <td className='align-middle'>
                          <strong className='text-theme'>{item.code}</strong>
                        </td>

                        <td className='align-middle'>{String(item.transactionDate || '').split('T')[0]}</td>

                        <td className='align-middle'>{i18n.language === 'en' ? item.peopleName : item.peopleName2}</td>

                        <td className='align-middle'>
                          {i18n.language === 'en' ? item.organizationName : item.organizationName2}
                        </td>
                        <td className='align-middle'>{getTypeOrInventoryDisplay(item)}</td>

                        <td className='align-middle'>
                          <span className='fw-bold text-theme'>{formatAmount(item.netAmount)}</span>
                        </td>

                        <td className='align-middle'>{getReceiveStatusBadge(item)}</td>

                        <td className='align-middle text-end'>
                          <div className='d-flex justify-content-end gap-2'>
                            {/* لو عندك permission خاص بالإرجاع غيّره هنا */}
                            <Can permission={'PURViewPurchaseOrderDetails'}>
                              <button
                                type='button'
                                className='btn btn-sm btn-outline-theme'
                                onClick={() => navigate(`${item.id}/AddUpdateViewPurchaseReturn`)}
                                title={t('Return')}
                              >
                                {/* <i className='fas fa-undo-alt me-2'></i>
                                {t('Return')} */}
                                <i className='bi bi-info-circle fa-lg me-2'></i>
                                {t('ViewDetails')}
                              </button>
                            </Can>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className='text-center py-4 text-muted'>
                        <i className='fa fa-inbox fa-2x mb-2'></i>
                        <br />
                        {activeFiltersCount > 0 ? t('NoSearchResults') : t('لا توجد أوامر شراء')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Loading>

        <CardFooter>
          <div className='text-theme'>
            <DynamicPagination tableKey={TABLE_KEY} maxVisiblePages={3} showItemsPerPage={true} />
          </div>
        </CardFooter>
      </Card>

      <DynamicFilter tableKey={TABLE_KEY} fields={fields} show={showFilters} onClose={() => setShowFilters(false)} />

      <NeatixModal show={showPoList} setShow={setShowPoList} bodyClassName='p-0' size='xl' showHeader={false}>
        <PoList />
      </NeatixModal>
    </>
  );
};

export default Index;
